import React from 'react';

import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Drawer, Fade, IconButton, Paper, Slide, Typography, useTheme } from '@mui/material';

interface CommentsDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  anchor?: 'left' | 'right';
  width?: number;
}

const CommentsDrawer: React.FC<CommentsDrawerProps> = ({
  open,
  onClose,
  title,
  children,
  anchor = 'right',
  width = 480,
}) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: width,
          borderTopLeftRadius: anchor === 'right' ? 16 : 0,
          borderBottomLeftRadius: anchor === 'right' ? 16 : 0,
          borderTopRightRadius: anchor === 'left' ? 16 : 0,
          borderBottomRightRadius: anchor === 'left' ? 16 : 0,
          background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          overflow: 'hidden',
        },
      }}
      transitionDuration={{
        enter: 300,
        exit: 250,
      }}
    >
      <Slide direction={anchor === 'right' ? 'left' : 'right'} in={open} timeout={300} mountOnEnter unmountOnExit>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 0,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="7" cy="7" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ChatIcon sx={{ fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                  {title}
                </Typography>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Paper>

          <Divider sx={{ borderColor: 'rgba(0,0,0,0.08)' }} />

          {/* Content */}
          <Fade in={open} timeout={400}>
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 3,
                '&::-webkit-scrollbar': {
                  width: 8,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0,0,0,0.05)',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: 4,
                  '&:hover': {
                    background: 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              {children}
            </Box>
          </Fade>
        </Box>
      </Slide>
    </Drawer>
  );
};

export default CommentsDrawer;
